import { useState } from "react";
import { useNotify } from "ra-core";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { cn } from "@/lib/utils";
import { getSupabaseClient } from "../../providers/supabase/supabase";
import { useAllOffers } from "../useOffers";
import { in90days, LOSS_REASONS, useSaveDeal } from "./boardData";
import { HelpTooltip } from "../help/HelpTooltip";

export type LossTarget = {
  id: number;
  key?: string | null;
  company_name?: string | null;
  offer_id: number;
  /** cambios extra que se guardan junto con la pérdida (p. ej. desde el formulario) */
  extra?: Record<string, unknown>;
};

/**
 * PRD B.5: motivo obligatorio para pasar a Perdido desde cualquier lugar.
 * Cancelar no cambia nada: la oportunidad se queda en su etapa.
 */
export const LossDialog = ({
  target,
  onClose,
}: {
  target: LossTarget | null;
  onClose: (lost: boolean) => void;
}) => {
  const [reason, setReason] = useState<string | null>(null);
  const [note, setNote] = useState("");
  const [remind, setRemind] = useState(true);
  const [saving, setSaving] = useState(false);
  const { data: offers } = useAllOffers();
  const { save } = useSaveDeal();
  const notify = useNotify();

  const close = (lost: boolean) => {
    setReason(null);
    setNote("");
    setRemind(true);
    onClose(lost);
  };

  const confirm = async () => {
    if (!target || !reason) return;
    const lostStage = offers?.find((o) => o.id === target.offer_id)?.lost_stage;
    if (!lostStage) return;
    setSaving(true);
    try {
      await save(target.id, {
        ...target.extra,
        stage: lostStage,
        lost_reason: reason,
        lost_note: note.trim() || null,
        revisit_at: remind ? in90days() : null,
      });
      // Perdida = se detiene su cadencia (las tareas hechas se conservan)
      const sb = getSupabaseClient();
      await sb
        .from("tasks")
        .delete()
        .eq("deal_id", target.id)
        .is("done_date", null)
        .not("enrollment_id", "is", null);
      await sb
        .from("sequence_enrollments")
        .update({ status: "terminada" })
        .eq("deal_id", target.id)
        .eq("status", "activa");
      notify(
        `${target.key ?? "Oportunidad"} perdida${remind ? " · se reabre en 90 días" : ""}`,
        { type: "success" },
      );
      close(true);
    } catch (e) {
      notify((e as Error).message, { type: "error" });
    }
    setSaving(false);
  };

  return (
    <Dialog open={!!target} onOpenChange={(o) => !o && !saving && close(false)}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle className="inline-flex items-center gap-1">
            ¿Por qué se perdió {target?.key ?? "la oportunidad"}?
            <HelpTooltip topic="deal.lost" />
          </DialogTitle>
          <DialogDescription>
            {target?.company_name ? `${target.company_name} · ` : ""}
            Obligatorio: así tus tasas y el termómetro aprenden.
          </DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-2 gap-2">
          {LOSS_REASONS.map(([value, label]) => (
            <Button
              key={value}
              variant="outline"
              className={cn(
                "h-11 whitespace-normal leading-tight",
                reason === value &&
                  "border-primary bg-accent text-accent-foreground",
              )}
              onClick={() => setReason(value)}
            >
              {label}
            </Button>
          ))}
        </div>
        <Textarea
          placeholder="Detalle (opcional): qué dijo exactamente"
          value={note}
          onChange={(e) => setNote(e.target.value)}
          rows={2}
        />
        <label className="flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            checked={remind}
            onChange={(e) => setRemind(e.target.checked)}
            className="size-4 accent-[var(--primary)]"
          />
          Recordarme en 90 días (se reabre esta misma oportunidad)
        </label>
        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => close(false)}
            disabled={saving}
          >
            Cancelar
          </Button>
          <Button onClick={confirm} disabled={!reason || saving}>
            Marcar como perdida
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
